import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import { Field, RichText, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { ManageCookieActionBt } from '../Cookie.styles';
import CookieCategory from './CookieCategoryItem';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ManageCookieProps, CookieBannerProps } from '../Cookie.types';
import { getCookieData } from '../../../services/CdpService';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../store/store';
import {
  acceptCustom,
  addOrRemovedAnalythicsCookie,
  deleteCookieData,
  setAcceptCookieSettings,
  isAcceptCookieOptinAndActive,
  setAcceptCookies,
  setOptinCookies,
  acceptAll,
} from '../../../lib/utils/cookieHelpers';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { hideCookieBanner } from '../../../features/cookieBannerSlice';
import { useRouter } from 'next/router';
import camelCase from 'lodash/camelCase';
import every from 'lodash/every';
import isEmpty from 'lodash/isEmpty';
import { getCookie } from '../../../lib/utils/enrollmentUtil';

const LoadingBackdrop = (props: { open: boolean }) => {
  return (
    <Backdrop sx={{ color: '#fff', zIndex: '9999' }} open={props.open}>
      <CircularProgress color="inherit" />
    </Backdrop>
  );
};

const ManageCookie = (): JSX.Element => {
  const [open, setOpen] = useState(false);
  const [CDPCookieCategory, setCDPCookieCategory] = useState<any[]>([]);
  const [isShowAcceptAll, setIsShowAcceptAll] = useState(false);
  const [isCookieAccept, setIsCookieAccept] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const { sitecoreContext }: any = useSitecoreContext();
  const googleTagManagerCode = (sitecoreContext?.siteSettings as any)?.googleTagManagerCode as any;
  const dispatch = useDispatch();
  const router = useRouter();

  const gtmId = (googleTagManagerCode as Field<string>)?.value || '';

  const globalCookiePreference = sitecoreContext?.globalData
    ?.globalCookiePreference as ManageCookieProps;
  const cookiePreference =
    (sitecoreContext?.cookiePreference as ManageCookieProps) || globalCookiePreference;

  const globalCookieBanner = sitecoreContext?.globalData?.globalCookieBanner as CookieBannerProps;
  const cookieBanner = (sitecoreContext?.cookieBanner as CookieBannerProps) || globalCookieBanner;
  const cookieExpiryDateInDays = cookieBanner?.cookieExpiryDateInDays?.value || 1;
  const isInfoEpr = (sitecoreContext?.route?.fields?.isInfoEpr as any)?.value || false;
  const potentialParticipantDetailsFromStore = useSelector(
    (state: RootState) => state.potentialParticipantDetails
  );
  const potentialParticipant_ID =
    potentialParticipantDetailsFromStore?.potentialParticipant_ID || '';

  const handleClose = (_event?: any, reason?: any) => {
    if (reason !== 'backdropClick') {
      setOpen(false);
    }
  };

  const cdpState = useSelector((state: RootState) => state.cdpData);
  const cookieBannerState = useSelector((state: RootState) => state.cookieBanner);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const acceptCookieSettingsCookie = JSON.parse(getCookie('AcceptCookieSettings') as string) || {};

  const AcceptCookieCategory = acceptCookieSettingsCookie?.cookieCategory || [];

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: 'white',
    width: isMobile ? '90%' : '70%',
    maxWidth: '1000px',
    borderRadius: '10px',
  };

  const isAllCookieEnabled = (): boolean => {
    if (!isInfoEpr && Array.isArray(CDPCookieCategory) && CDPCookieCategory.length > 0) {
      const isOptInValues = CDPCookieCategory.map((item) => item.isOptin);
      const isAllTrue = isOptInValues.every((item) => item === true);
      return isAllTrue;
    } else if (
      isInfoEpr &&
      Array.isArray(AcceptCookieCategory) &&
      AcceptCookieCategory.length > 0
    ) {
      const isOptInValues = AcceptCookieCategory.map((item) => item.isOptin);
      const isAllTrue = isOptInValues.every((item) => item === true);
      return isAllTrue;
    }
    return false;
  };

  const AllowButtonComponent = () => {
    const isAllTrue = (!isEmpty(isCookieAccept) && every(Object.values(isCookieAccept))) || false;
    return (
      <ManageCookieActionBt
        className="btn"
        variant="contained"
        onClick={async () => {
          try {
            const cookieCategories = cookiePreference?.cookieCategory;
            const categoryNames: string[] = [];
            if (Array.isArray(cookieCategories)) {
              cookieCategories.forEach((item) => {
                const categoryName = item?.fields?.name?.value || '';
                categoryName && categoryNames.push(categoryName);
              });
            }
            const guestType = cdpState?.guestType || 'visitor';
            await acceptAll(
              potentialParticipant_ID,
              categoryNames,
              guestType,
              cookieExpiryDateInDays,
              isInfoEpr
            );
            await addOrRemovedAnalythicsCookie(gtmId);
            setAcceptCookies(true, cookieExpiryDateInDays);
            setOptinCookies(true, true);
          } catch (_error) {
          } finally {
            dispatch(hideCookieBanner());
            handleClose();
          }
        }}
        sx={{
          display: isShowAcceptAll || isAllTrue ? 'none' : 'inherit',
        }}
      >
        <Text field={cookiePreference?.allowAllLabel} />
      </ManageCookieActionBt>
    );
  };

  const ConfirmButtonComponent = () => (
    <ManageCookieActionBt
      className="btn"
      variant="contained"
      onClick={async () => {
        try {
          const guestType = cdpState?.guestType || 'visitor';
          await checkAndManageGTMCookie();
          await acceptCustom(
            potentialParticipant_ID,
            !isInfoEpr ? CDPCookieCategory : AcceptCookieCategory,
            guestType,
            cookieExpiryDateInDays,
            isInfoEpr
          );
          const isAllCookiesOptIn = isAllCookieEnabled();
          const isAnalyticsCookiesOptIn = isAcceptCookieOptinAndActive('Non-Essential Cookies');
          setAcceptCookies(isAllCookiesOptIn, cookieExpiryDateInDays);
          await addOrRemovedAnalythicsCookie(gtmId);
          if (isAnalyticsCookiesOptIn) {
            setOptinCookies(isAnalyticsCookiesOptIn, isAnalyticsCookiesOptIn);
          }
        } catch (error) {
        } finally {
          dispatch(hideCookieBanner());
          handleClose();
        }
      }}
    >
      <Text field={cookiePreference?.saveButtonLabel} />
    </ManageCookieActionBt>
  );

  const deleteGTMScript = () => {
    document.querySelectorAll('script').forEach((script: any) => {
      if (
        script?.textContent?.includes(gtmId) ||
        script?.textContent?.includes('facebook.net') ||
        script?.src?.includes('googletagmanager.com') ||
        script?.src?.includes('facebook.net')
      )
        script?.remove();
    });
  };

  const checkAndManageGTMCookie = () => {
    const acceptCookieSettings = (CDPCookieCategory as any).filter(
      (x: any) => x.categoryType === 'Non-Essential Cookies'
    )[0];

    if (!acceptCookieSettings?.isOptin) {
      (window as any).dataLayerClone = (window as any).dataLayer;
      (window as any).dataLayer = [];
      deleteGTMScript();
    }
  };

  useEffect(() => {
    const handleOpenDialog = (e: any) => {
      e.preventDefault();
      setOpen(true);
    };
    const elementsToOpenMangeCookie = document.querySelectorAll('a.manage-cookies');
    elementsToOpenMangeCookie.forEach((element) => {
      element.addEventListener('click', handleOpenDialog);
    });
    return () => {
      elementsToOpenMangeCookie.forEach((element) => {
        element.removeEventListener('click', handleOpenDialog);
      });
    };
  }, [cookieBannerState, router]);

  useEffect(() => {
    const getCookieDataFromCDP = async () => {
      try {
        setIsLoading(true);
        const guestType = cdpState?.guestType || 'visitor';
        const newCookieCategory = await getCookieData(potentialParticipant_ID, guestType);
        if (Array.isArray(newCookieCategory?.data?.cookieCategory)) {
          setCDPCookieCategory(newCookieCategory?.data?.cookieCategory);
        }
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    };
    if (open && !isInfoEpr) {
      getCookieDataFromCDP();
    }
  }, [open]);

  useEffect(() => {
    setIsShowAcceptAll(isAllCookieEnabled());
  }, [CDPCookieCategory]);

  /**
   * Updates the isOptIn property for a given cookie category in local state
   */
  const updateIsOptin = (categoryTypeToUpdate: string, newIsOptinValue: boolean) => {
    const updatedCategories = !isInfoEpr
      ? CDPCookieCategory.map((category) => {
          if (category.categoryType === categoryTypeToUpdate) {
            return { ...category, isOptin: newIsOptinValue };
          }
          return category;
        })
      : AcceptCookieCategory.map((category: any) => {
          if (category.categoryType === categoryTypeToUpdate) {
            return { ...category, isOptin: newIsOptinValue };
          }
          return category;
        });
    // Delete all cookies in that category if new optin is false
    if (!newIsOptinValue) {
      const exceptCookie = ['_fbp', '_ga'];
      const cookieCategories = cookiePreference?.cookieCategory;
      if (Array.isArray(cookieCategories)) {
        const currentCategory = cookieCategories.find(
          (cat) => cat.fields?.name?.value === categoryTypeToUpdate
        );
        const cookieNamesArr = currentCategory?.fields.cookies.map(
          (cookie) => cookie.fields?.nameValue?.value || ''
        );
        if (Array.isArray(cookieNamesArr)) {
          cookieNamesArr.forEach((cookieName) => {
            if (!exceptCookie.includes(cookieName)) deleteCookieData(cookieName || '');
          });
        }
      }
    }

    if (isInfoEpr) {
      acceptCookieSettingsCookie.cookieCategory = updatedCategories;
      setAcceptCookieSettings(acceptCookieSettingsCookie, cookieExpiryDateInDays);
    } else {
      setCDPCookieCategory(updatedCategories);
      setAcceptCookieSettings(acceptCookieSettingsCookie, cookieExpiryDateInDays);
    }
  };

  /**
   * Matches the coookie category name from sitecore context to cookie category consent data from CDP
   */
  const getRespectiveCDPCategory = (categoryName: string) => {
    if (
      !isInfoEpr &&
      categoryName &&
      Array.isArray(CDPCookieCategory) &&
      CDPCookieCategory.length > 0
    ) {
      return CDPCookieCategory.find((item: any) => item?.categoryType === categoryName);
    } else if (
      isInfoEpr &&
      categoryName &&
      Array.isArray(AcceptCookieCategory) &&
      AcceptCookieCategory.length > 0
    ) {
      return AcceptCookieCategory.find((item: any) => item?.categoryType === categoryName);
    }
    return {};
  };

  const transformCookieCategory = (cookieCategory: any[]): any[] => {
    if (Array.isArray(cookieCategory) && cookieCategory.length > 0) {
      const transformedCategory = cookieCategory.map((item) => {
        const removeSpaces = (input: string) => input.replace(/\s+/g, '');
        const categoryName = item?.fields?.name?.value || '';
        const CDPCookieData = getRespectiveCDPCategory(categoryName);
        const isEssential = removeSpaces(categoryName).toLowerCase() === 'essentialcookies';
        const stateName = camelCase(item.name);
        const isEnabled = CDPCookieData?.isOptin || !!isCookieAccept?.[stateName] || false;
        const updateCategoryConsent = (value: boolean) => {
          setIsCookieAccept((prevState: any) => ({ ...prevState, [stateName]: value }));
          updateIsOptin(categoryName, !isEnabled || value);
        };
        return { ...item, isEssential, isEnabled, updateCategoryConsent };
      });
      return transformedCategory;
    }
    return [];
  };
  const category = transformCookieCategory(cookiePreference?.cookieCategory);

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        disableEscapeKeyDown
      >
        <Box sx={style}>
          <LoadingBackdrop open={isLoading} />
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            padding={isMobile ? '1rem' : '2rem'}
          >
            <Typography
              variant="h4"
              sx={{
                lineHeight: '2rem',
                fontWeight: '500',
              }}
            >
              <Text field={cookiePreference?.heading} />
            </Typography>
            {!isMobile && <AllowButtonComponent />}
          </Box>
          <Box
            sx={{
              paddingInline: isMobile ? '1rem' : '2rem',
              maxHeight: isMobile ? '60vh' : '70vh',
              overflowY: 'auto',
              overflowX: 'hidden',
              zIndex: 100,
            }}
          >
            <Box sx={{ fontSize: '1rem', lineHeight: '1.5rem', mt: '4px' }}>
              <RichText field={cookiePreference?.description} />
            </Box>
            {/* Category Box */}
            <Box mt={'2rem'}>
              {category &&
                category.map((item, index) => {
                  return (
                    <CookieCategory
                      key={index}
                      title={item?.fields?.heading}
                      description={item?.fields?.description}
                      viewCookiesButton={cookiePreference?.viewCookiesLabel}
                      alwaysActiveButton={cookiePreference?.alwaysActiveLabel}
                      isEssential={item?.isEssential}
                      cookieList={item?.fields?.cookies}
                      isEnabled={item?.isEnabled}
                      updateCategoryConsent={item?.updateCategoryConsent}
                    />
                  );
                })}
            </Box>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              borderRadius: '0 0 10px 10px',
              position: 'relative',
              height: 'auto',
              bottom: '0px',
              background: (theme) => theme.palette.neutral0.main,
              zIndex: 100,
              padding: '1rem',
              display: 'flex',
              justifyContent: 'flex-end',
              paddingInline: isMobile ? '' : '2rem',
              flexDirection: isMobile ? 'column' : 'row',
              rowGap: isMobile ? '8px' : '',
            }}
          >
            {isMobile && <AllowButtonComponent />}
            <ConfirmButtonComponent />
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ManageCookie;
